import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { apiFetch, apiPost, friendlyError, type ApiError } from "../../api/client.js";
import { QuotaBar } from "./quota-bar.js";
import { type VmDisk } from "./vmUtils.js";

interface CreateOptions {
  defaults: { node: string };
  quota: { cpu_cores: number; ram_mb: number; disk_gb: number };
  usage: { cpu_cores: number; ram_mb: number; disk_gb: number } | null;
  usage_unavailable?: boolean;
  remaining: { cpu_cores: number; ram_mb: number; disk_gb: number } | null;
}

interface AddDiskModalProps {
  vmid: number;
  opened: boolean;
  onClose: () => void;
  onMutated: () => void;
  dataDisks: VmDisk[];
  bootSlot: string | null;
  maxExtraDisks: number;
}

function isApiError(err: unknown): err is ApiError {
  return !!err && typeof err === "object" && "status" in err;
}

/*
 * Add disk (Phase 26). Fetches /api/create-options on open for the
 * customer's remaining quota, then offers a size input (GB). Phase 29:
 * storage is server-selected — first pool of the customer's effective set
 * present on the VM's node; the success toast names where the disk landed.
 * `dataDisks`/`bootSlot`/`maxExtraDisks` arrive from the LIVE parent props
 * (re-polled every 5s) and are re-derived on every render — never captured
 * at open.
 *
 * Submit error mapping (matches the backend's response shapes):
 *   409 quota_exceeded      -> "Quota exceeded: Disk +N GB."
 *   409 no_eligible_storage -> no pool on this VM's node (contact support)
 *   409 disk_limit_reached  -> orange-worthy limit message
 *   409 no_free_slot        -> message under the form
 *   409 vm_locked           -> "Another operation is in progress..."
 *   409 not_supported       -> templates cannot be modified
 *   404 orphaned / disk_not_found -> close modal (guest gone)
 *   502 pve_forbidden       -> message under the form
 *   502 pve_unavailable     -> "Proxmox is unavailable..."
 *   429                     -> "Too many requests..."
 *   401/403                 -> close modal (session expired)
 */
export function AddDiskModal({
  vmid,
  opened,
  onClose,
  onMutated,
  dataDisks,
  bootSlot,
  maxExtraDisks,
}: AddDiskModalProps) {
  const [options, setOptions] = useState<CreateOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sizeGb, setSizeGb] = useState<number | "">("");
  const [formError, setFormError] = useState<string | undefined>(undefined);

  async function loadOptions() {
    setLoading(true);
    try {
      const data = await apiFetch<CreateOptions>("/api/create-options");
      setOptions(data);
      setSizeGb(10);
    } catch (err) {
      const e = err as ApiError;
      if (e.status === 401 || e.status === 403) {
        onClose();
        return;
      }
      notifications.show({
        color: "red",
        message: friendlyError(err, "Could not load options."),
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (opened) {
      setFormError(undefined);
      void loadOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // Derived from props at render time — the parent re-polls every 5s.
  const extras = dataDisks.filter((d) => d.slot !== bootSlot);
  const atLimit = extras.length >= maxExtraDisks;

  const usageUnavailable = !!options?.usage_unavailable;
  const remaining = options?.remaining ?? { cpu_cores: 0, ram_mb: 0, disk_gb: 0 };
  const quota = options?.quota ?? { cpu_cores: 0, ram_mb: 0, disk_gb: 0 };

  const reqDisk = typeof sizeGb === "number" ? sizeGb : 0;
  const overDisk = reqDisk > remaining.disk_gb;

  const canSubmit =
    !submitting &&
    !usageUnavailable &&
    !atLimit &&
    typeof sizeGb === "number" &&
    sizeGb >= 1 &&
    !overDisk;

  async function submit() {
    if (!canSubmit || typeof sizeGb !== "number") return;
    setSubmitting(true);
    setFormError(undefined);
    try {
      const res = await apiPost<{ disk: { slot: string; size_gb: number } }>(
        `/api/vms/${vmid}/disks`,
        { size_gb: sizeGb }
      );
      notifications.show({
        color: "green",
        message: `Disk added (slot ${res.disk.slot}, ${res.disk.size_gb} GB).`,
      });
      onMutated();
      onClose();
    } catch (err) {
      if (!isApiError(err)) {
        setFormError("Couldn't reach the server.");
        return;
      }
      const e = err;
      const detail = e.details as Record<string, unknown> | undefined;
      if (e.status === 401 || e.status === 403) {
        onClose();
      } else if (e.status === 409 && e.error === "quota_exceeded") {
        const excess = detail?.excess as Record<string, number> | undefined;
        const fields: string[] = [];
        if (excess?.cpu_cores) fields.push(`CPU +${excess.cpu_cores}`);
        if (excess?.ram_mb) fields.push(`RAM +${excess.ram_mb} MB`);
        if (excess?.disk_gb) fields.push(`Disk +${excess.disk_gb} GB`);
        setFormError(`Quota exceeded: ${fields.join(", ") || "insufficient headroom"}.`);
      } else if (e.status === 409 && e.error === "no_quota") {
        setFormError("Your account has no quota configured. Contact support.");
      } else if (e.status === 409 && e.error === "no_eligible_storage") {
        setFormError(
          typeof detail?.message === "string"
            ? detail.message
            : "No eligible storage on this server's node — contact support."
        );
      } else if (e.status === 409 && e.error === "disk_limit_reached") {
        setFormError(
          typeof detail?.message === "string"
            ? detail.message
            : `Disk limit reached (max ${maxExtraDisks} extra).`
        );
      } else if (e.status === 409 && e.error === "no_free_slot") {
        setFormError("This server has no free disk slots left.");
      } else if (e.status === 409 && e.error === "vm_locked") {
        setFormError(
          typeof detail?.message === "string"
            ? detail.message
            : "Another operation is in progress; try again shortly."
        );
      } else if (e.status === 409 && e.error === "not_supported") {
        setFormError(
          typeof detail?.message === "string" ? detail.message : "Templates cannot be modified."
        );
      } else if (e.status === 404 && (e.error === "orphaned" || e.error === "disk_not_found")) {
        onClose();
      } else if (e.status === 502 && e.error === "pve_forbidden") {
        setFormError("Proxmox denied the disk operation. Contact support.");
      } else if (e.status === 502 && e.error === "pve_unavailable") {
        setFormError("Proxmox is unavailable. Please try again.");
      } else if (e.status === 429) {
        setFormError("Too many requests. Please wait a minute and try again.");
      } else {
        setFormError(friendlyError(e, "Couldn't add disk."));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add disk"
      size="lg"
      closeOnClickOutside={!submitting}
      closeOnEscape={!submitting}
    >
      <Stack gap="md">
        {loading && (
          <Text c="dimmed" size="sm">
            Loading options…
          </Text>
        )}

        {options && usageUnavailable && (
          <Alert color="yellow" variant="light" p="sm">
            Can't compute current usage (Proxmox unreachable). Please try again later.
          </Alert>
        )}

        {options && !usageUnavailable && (
          <>
            <Alert color="gray" variant="light" p="sm">
              <Stack gap="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Quota remaining
                </Text>
                <QuotaBar
                  label="Disk"
                  used={quota.disk_gb - remaining.disk_gb}
                  total={quota.disk_gb}
                  unit="GB"
                />
              </Stack>
            </Alert>

            <NumberInput
              label="Size (GB)"
              value={sizeGb}
              onChange={(v) => setSizeGb(typeof v === "number" ? v : "")}
              min={1}
              max={10000}
              error={overDisk ? `Only ${remaining.disk_gb} GB remaining.` : undefined}
              disabled={submitting}
            />

            {atLimit && (
              <Text size="xs" c="orange">
                Disk limit reached (max {maxExtraDisks} extra).
              </Text>
            )}
            {formError && (
              <Text size="sm" c="red">
                {formError}
              </Text>
            )}

            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={() => void submit()} loading={submitting} disabled={!canSubmit}>
                Add disk
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
